import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form } from '@components/atoms/Form';
import { ErrorMessages } from '@components/atoms/ErrorMessages';
import { Space } from '@components/atoms/Space';
import { Input, InputStates, InputTypes, InputVariants } from '@components/atoms/Input';
import { Typography } from '@components/atoms/Typography';
import { Select, SelectType } from '@components/atoms/Select';
import { Button, ButtonMode } from '@components/atoms/Button';
import { TranslationTabs } from '../TranslationTabs';
import { SITE_URLS } from '@constants';
import { useCustomNavigate } from '@hooks/useCustomNavigate';
import { useData } from '@hooks/useData';
import { useNotifications } from '@hooks/useNotifications';
import { InputErrors, Translations } from '@types';
import { useCreateService } from '@services/useCreateService';
import { useEditService } from '@services/useEditService';
import { useGetAllMyAccountStores } from '@services/useGetAllMyAccountStores';
import { useGetAllServicesCategories } from '@services/useGetAllServicesCategories';
import { updateTranslation } from '@utils/translation';

interface ServiceFormErrors {
    general: string;
    name: InputErrors;
    category: InputErrors;
    merchant: InputErrors;
    state: InputErrors;
    scheduleType: InputErrors;
    link: InputErrors;
    description: InputErrors;
    serviceHours: InputErrors;
    serviceMinutes: InputErrors;
    isCumulative: InputErrors;
    price: InputErrors;
}

type ServiceFormData = {
    service_hours:
        | '0'
        | '1'
        | '2'
        | '3'
        | '4'
        | '5'
        | '6'
        | '7'
        | '8'
        | '9'
        | '10'
        | '11'
        | '12'
        | '13'
        | '14'
        | '15'
        | '16'
        | '17'
        | '18'
        | '19'
        | '20'
        | '21'
        | '22'
        | '23'
        | '24';
    service_minutes: '0' | '15' | '30' | '45';
};

interface ServiceFormProps {
    edit: boolean;
}

export const ServiceForm: FC<ServiceFormProps> = ({ edit }) => {
    const { navigate } = useCustomNavigate();
    const { showDefaultNotification } = useNotifications();
    const {
        login,
        myAccountServiceDetail,
        isLoadingMyAccountServiceDetail,
        createdService,
        allServicesCategories,
        isLoadingAllServicesCategories,
        allMyAccountStores,
        isLoadingAllMyAccountStores,
        allLanguages,
        allSettings,
    } = useData();
    const {
        request: requestCreateService,
        errorMessage: errorMessageCreate,
        validationErrors: validationErrorsCreate,
        success: successMessageCreate,
        lastResponseTime: lastCreate,
    } = useCreateService();
    const {
        request: requestEditService,
        errorMessage: errorMessageEdit,
        validationErrors: validationErrorsEdit,
        success: successMessageEdit,
        lastResponseTime: lastEdit,
    } = useEditService();
    const { request: requestAllServicesCategories } = useGetAllServicesCategories();
    const { request: requestAllMyAccountStores } = useGetAllMyAccountStores();
    const { t } = useTranslation();

    const automaticTranslations = allSettings?.data?.module_automatic_translations?.active;
    const languagesToTranslate = allLanguages?.data?.filter((item) => item.default !== 1) || [];
    const servicePaymentsSetting = allSettings?.data?.module_services?.payments;

    const translations = {
        nameLabel: t('manage-services-form.service.name.label'),
        nameRequiredError: t('manage-services-form.service.name.required-error'),
        stateLabel: t('manage-services-form.service.state.label'),
        stateRequiredError: t('manage-services-form.service.state.required-error'),
        categoryLabel: t('manage-services-form.service.category.label'),
        categoryRequiredError: t('manage-services-form.service.category.required-error'),
        merchantLabel: t('manage-services-form.service.merchant.label'),
        merchantRequiredError: t('manage-services-form.service.merchant.required-error'),
        scheduleTypeLabel: t('manage-services-form.service.schedule-type.label'),
        linkLabel: t('manage-services-form.service.link.label'),
        linkRequiredError: t('manage-services-form.service.link.required-error'),
        descriptionLabel: t('manage-services-form.service.description.label'),
        serviceHoursLabel: t('manage-services-form.service.service-hours.label'),
        serviceHoursRequiredError: t('manage-services-form.service.service-minutes.required-error'),
        serviceMinutesLabel: t('manage-services-form.service.service-minutes.label'),
        serviceMinutesRequiredError: t('manage-services-form.service.service-minutes.required-error'),
        isCumulativeLabel: t('manage-services-form.service.is-cumulative.label'),
        isCumulativeDescription: t('manage-services-form.service.is-cumulative.description'),
        isCumulative: t('manage-services-form.service.is-cumulative.yes'),
        isNotCumulative: t('manage-services-form.service.is-cumulative.no'),
        priceLabel: t('manage-services-form.service.price.label'),
        priceRequiredError: t('manage-services-form.service.price.required-error'),
        nameTranslatedLabel: t('manage-services-form.translations.name.label'),
        nameTranslatedRequiredError: t('manage-services-form.translations.name.required-error'),
        descriptionTranslatedLabel: t('manage-services-form.translations.description.label'),
        submitButton: t('manage-services-form.submit.button'),
        default: t('select-default-option'),
        serviceStateActive: t('service-state.active'),
        serviceStateInactive: t('service-state.inactive'),
        serviceStatePending: t('service-state.pending'),
        serviceStateRejected: t('service-state.rejected'),
        scheduleTypePreBook: t('service-schedule-type.pre-book'),
        scheduleTypeExternalLink: t('service-schedule-type.external-link'),
        scheduleTypeEffectiveReserve: t('service-schedule-type.effective-reserve'),
        noSchedule: t('service-schedule-type.no-schedule'),
    };

    const formRef = useRef<HTMLFormElement>(null);
    const [name, setName] = useState<string>('');
    const [category, setCategory] = useState<string>('');
    const [merchant, setMerchant] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [state, setState] = useState<string | null>(null);
    const [scheduleType, setScheduleType] = useState<string | null>(null);
    const [link, setLink] = useState<string>('');
    const [serviceHours, setServiceHours] = useState<ServiceFormData['service_hours']>('0');
    const [serviceMinutes, setServiceMinutes] = useState<ServiceFormData['service_minutes']>('0');
    const [isCumulative, setIsCumulative] = useState<boolean>(true);
    const [price, setPrice] = useState<number | null>(null);
    const [nameTranslations, setNameTranslations] = useState<Translations>([]);
    const [descriptionTranslations, setDescriptionTranslations] = useState<Translations>([]);

    // call services, if we don't have the information yet
    useEffect(() => {
        !allServicesCategories && !isLoadingAllServicesCategories && requestAllServicesCategories();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allServicesCategories]);

    useEffect(() => {
        !allMyAccountStores && !isLoadingAllMyAccountStores && requestAllMyAccountStores();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allMyAccountStores]);

    // initially, update fields
    useEffect(() => {
        if (edit) {
            setName(myAccountServiceDetail?.data?.name ?? '');
            setCategory(myAccountServiceDetail?.data?.category?.uuid ?? '');
            setMerchant(myAccountServiceDetail?.data?.merchant?.uuid ?? '');
            setState(myAccountServiceDetail?.data?.status ?? '');
            setScheduleType(myAccountServiceDetail?.data?.scheduleType ?? '');
            setLink(myAccountServiceDetail?.data?.link?.url ?? '');
            setDescription(myAccountServiceDetail?.data?.description ?? '');
            setServiceHours(myAccountServiceDetail?.data?.serviceHours ?? '0');
            setServiceMinutes(myAccountServiceDetail?.data?.serviceMinutes ?? '0');
            setIsCumulative(myAccountServiceDetail?.data?.isCumulative ?? true);
            setPrice(myAccountServiceDetail?.data?.price ?? null);
            setNameTranslations(
                myAccountServiceDetail?.data?.translations
                    ?.filter((translation) => translation.name === 'name')
                    .map((translation) => {
                        return { languageCode: translation.language_code || '', translation: translation.text || '' };
                    }) || []
            );
            setDescriptionTranslations(
                myAccountServiceDetail?.data?.translations
                    ?.filter((translation) => translation.name === 'description')
                    .map((translation) => {
                        return { languageCode: translation.language_code || '', translation: translation.text || '' };
                    }) || []
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [myAccountServiceDetail]);

    const stores = (allMyAccountStores?.data || [])
        .filter((item) => {
            const isMyStore = item.uuid === myAccountServiceDetail?.data?.merchant?.uuid;
            return (!item.is_product_store && item.status !== 'pending') || isMyStore;
        })
        .map((item) => ({
            value: item.uuid || '',
            text: item.name || '',
        }));

    // errors to be displayed in the form
    const [errors, setErrors] = useState<ServiceFormErrors>({
        general: '',
        name: [],
        category: [],
        merchant: [],
        state: [],
        link: [],
        scheduleType: [],
        description: [],
        serviceHours: [],
        serviceMinutes: [],
        isCumulative: [],
        price: [],
    });

    const validServiceHours: ServiceFormData['service_hours'][] = [
        '0',
        '1',
        '2',
        '3',
        '4',
        '5',
        '6',
        '7',
        '8',
        '9',
        '10',
        '11',
        '12',
        '13',
        '14',
        '15',
        '16',
        '17',
        '18',
        '19',
        '20',
        '21',
        '22',
        '23',
        '24',
    ];
    const validServiceMinutes: ServiceFormData['service_minutes'][] = ['0', '15', '30', '45'];

    const handleServiceHoursChange = (value: string) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (validServiceHours.includes(value as any)) {
            setServiceHours(value as ServiceFormData['service_hours']);

            if (value === '24') {
                setServiceMinutes('0');
            }
        }
    };

    const handleServiceMinutesChange = (value: string) => {
        if (serviceHours !== '24' || value === '0') {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if (validServiceMinutes.includes(value as any)) {
                setServiceMinutes(value as ServiceFormData['service_minutes']);
            }
        }
    };

    const handleOnClickSaveService = () => {
        const errors = validateForm();

        if (
            errors.name.length === 0 &&
            errors.category.length === 0 &&
            errors.merchant.length === 0 &&
            errors.state.length === 0 &&
            errors.link.length === 0 &&
            errors.description.length === 0 &&
            errors.scheduleType.length === 0 &&
            errors.serviceHours.length === 0 &&
            errors.serviceMinutes.length === 0 &&
            errors.isCumulative.length === 0 &&
            errors.price.length === 0
        ) {
            formRef?.current?.requestSubmit();
        }
    };

    const validateForm: () => ServiceFormErrors = () => {
        const newErrors: ServiceFormErrors = {
            general: '',
            name: [],
            category: [],
            merchant: [],
            state: [],
            scheduleType: [],
            link: [],
            description: [],
            serviceHours: [],
            serviceMinutes: [],
            isCumulative: [],
            price: [],
        };

        !name.trim() && newErrors.name.push(translations.nameRequiredError);
        !category.trim() && newErrors.category.push(translations.categoryRequiredError);
        !merchant.trim() && newErrors.merchant.push(translations.merchantRequiredError);
        !state && newErrors.state.push(translations.stateRequiredError);
        !serviceHours.trim() && newErrors.serviceHours.push(translations.serviceHoursRequiredError);
        !serviceMinutes.trim() && newErrors.serviceMinutes.push(translations.serviceMinutesRequiredError);

        if (scheduleType === 'effective_reserve') {
            !price && newErrors.price.push(translations.priceRequiredError);
        } else if (scheduleType === 'external_link') {
            !link.trim() && newErrors.link.push(translations.linkRequiredError);
        }

        setErrors(newErrors);

        return newErrors;
    };

    const submitForm: () => void = async () => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const inputTranslations: any = {};

        nameTranslations.forEach((item) => (inputTranslations[`name_${item.languageCode}`] = item.translation));
        descriptionTranslations.forEach((item) => (inputTranslations[`description_${item.languageCode}`] = item.translation));

        if (edit === true) {
            const data = {
                name: name,
                category_uuid: category,
                merchant_uuid: merchant,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                status: (state || '') as any,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                schedule_type: (scheduleType || '') as any,
                url: link,
                description: description,
                service_hours: serviceHours,
                service_minutes: serviceMinutes,
                is_cumulative: isCumulative,
                price: price,
                ...inputTranslations,
            };

            requestEditService({ uuid: myAccountServiceDetail?.data?.uuid || '' }, data);
        } else {
            const data: {
                name: string;
                status: 'active' | 'inactive';
                category_uuid: string;
                merchant_uuid: string;
                schedule_type: 'pre_book' | 'external_link' | 'effective_reserve';
                url: string;
                description: string;
                service_hours:
                    | '0'
                    | '1'
                    | '2'
                    | '3'
                    | '4'
                    | '5'
                    | '6'
                    | '7'
                    | '8'
                    | '9'
                    | '10'
                    | '11'
                    | '12'
                    | '13'
                    | '14'
                    | '15'
                    | '16'
                    | '17'
                    | '18'
                    | '19'
                    | '20'
                    | '21'
                    | '22'
                    | '23'
                    | '24';
                service_minutes: '0' | '15' | '30' | '45';
                is_cumulative: boolean;
                price: number | undefined;
            } = {
                name,
                status: state as 'active' | 'inactive',
                category_uuid: category,
                merchant_uuid: merchant,
                schedule_type: scheduleType as 'pre_book' | 'external_link' | 'effective_reserve',
                url: link,
                description,
                service_hours: serviceHours,
                service_minutes: serviceMinutes,
                is_cumulative: isCumulative,
                price: price !== null ? price : undefined,
            };

            requestCreateService(data);
        }
    };

    // reset price errors when changing schedule type
    useEffect(() => {
        setErrors((previous) => {
            return { ...previous, price: [] };
        });
    }, [scheduleType]);

    // if create service returned successfully, dispatch notification and go to service's edit page
    // if not, show errors
    useEffect(() => {
        if (lastCreate) {
            if (errorMessageCreate || validationErrorsCreate) {
                setErrors({
                    general: errorMessageCreate as string,
                    name: validationErrorsCreate?.name || [],
                    category: validationErrorsCreate?.category_id || [],
                    merchant: validationErrorsCreate?.store_id || [],
                    state: validationErrorsCreate?.status || [],
                    scheduleType: validationErrorsCreate?.schedule_type || [],
                    link: validationErrorsCreate?.url || [],
                    description: validationErrorsCreate?.description || [],
                    serviceHours: validationErrorsCreate?.service_hours || [],
                    serviceMinutes: validationErrorsCreate?.service_minutes || [],
                    isCumulative: validationErrorsCreate?.is_cumulative || [],
                    price: validationErrorsCreate?.price || [],
                });
            } else if (login && createdService !== null) {
                showDefaultNotification({ type: 'success', message: successMessageCreate || '' });
                navigate(`/${SITE_URLS.myServices}`);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastCreate, createdService]);

    // if edit service returned successfully, dispatch notification and update
    // if not, show error
    useEffect(() => {
        if (lastEdit) {
            if (errorMessageEdit || validationErrorsEdit) {
                setErrors({
                    general: errorMessageEdit as string,
                    name: validationErrorsEdit?.name || [],
                    category: validationErrorsEdit?.category_id || [],
                    merchant: validationErrorsEdit?.store_id || [],
                    state: validationErrorsEdit?.status || [],
                    link: validationErrorsEdit?.url || [],
                    scheduleType: validationErrorsEdit?.schedule_type || [],
                    description: validationErrorsEdit?.description || [],
                    serviceHours: validationErrorsEdit?.service_hours || [],
                    serviceMinutes: validationErrorsEdit?.service_minutes || [],
                    isCumulative: validationErrorsEdit?.is_cumulative || [],
                    price: validationErrorsEdit?.price || [],
                });
            } else if (login) {
                showDefaultNotification({ type: 'success', message: successMessageEdit || '' });
                navigate(`/${SITE_URLS.myServices}`);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastEdit]);

    return (
        <Form formRef={formRef} onSubmit={() => submitForm()}>
            <>
                {errors.general && (
                    <>
                        <ErrorMessages messages={[errors.general]} />
                        <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                    </>
                )}
            </>

            <div className="row">
                {/* name */}
                <div className="col-12">
                    <Input
                        type={InputTypes.text}
                        variant={InputVariants.labeledInput}
                        label={`${translations.nameLabel}*`}
                        value={name}
                        onChange={(value) => setName(value)}
                        disabled={isLoadingMyAccountServiceDetail}
                        state={errors.name.length > 0 ? InputStates.error : undefined}
                        errorMessages={errors.name}
                    />
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                </div>

                {/* description */}
                <div className="col-12">
                    <Input
                        type={InputTypes.text}
                        variant={InputVariants.labeledInput}
                        label={translations.descriptionLabel}
                        value={description}
                        disabled={isLoadingMyAccountServiceDetail}
                        onChange={(value) => setDescription(value)}
                        state={errors.description.length > 0 ? InputStates.error : undefined}
                        errorMessages={errors.description}
                    />
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                </div>

                {/* category */}
                <div className="col-md-6 col-12">
                    <div>
                        <Typography color="muted" fontSize="12">
                            <>{translations.categoryLabel}*</>
                        </Typography>
                        <Select
                            classes="w-100"
                            type={SelectType.variant1}
                            disabled={
                                isLoadingMyAccountServiceDetail ||
                                allServicesCategories === null ||
                                isLoadingAllServicesCategories
                            }
                            options={[
                                { groupName: '', groupOptions: [{ value: '', text: translations.default }] },
                                ...(allServicesCategories?.data || []).map((group) => {
                                    return {
                                        groupName: group.name || '',
                                        groupOptions:
                                            group.children?.map((item) => {
                                                return { value: item.uuid || '', text: item.name || '' };
                                            }) || [],
                                    };
                                }),
                            ]}
                            value={category || undefined}
                            onChange={(value) => setCategory(value)}
                        />
                        {errors.category.length > 0 && <ErrorMessages messages={errors.category} />}
                    </div>

                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                </div>

                {/* store */}
                <div className="col-md-6 col-12">
                    <div>
                        <Typography color="muted" fontSize="12">
                            <>{translations.merchantLabel}*</>
                        </Typography>
                        <Select
                            classes="w-100"
                            type={SelectType.variant1}
                            disabled={
                                isLoadingMyAccountServiceDetail || allMyAccountStores === null || isLoadingAllMyAccountStores
                            }
                            options={[{ value: '', text: translations.default }, ...stores]}
                            value={merchant || undefined}
                            onChange={(value) => setMerchant(value)}
                        />
                        {errors.merchant.length > 0 && <ErrorMessages messages={errors.merchant} />}
                    </div>
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                </div>
                {/* state */}
                <div className="col-md-6 col-12">
                    <div>
                        <Typography color="muted" fontSize="12">
                            <>{translations.stateLabel}*</>
                        </Typography>
                        <Select
                            classes="w-100"
                            type={SelectType.variant1}
                            disabled={isLoadingMyAccountServiceDetail}
                            options={[
                                { value: '', text: translations.default },
                                { value: 'active', text: translations.serviceStateActive },
                                { value: 'inactive', text: translations.serviceStateInactive },
                                { value: 'pending', text: translations.serviceStatePending },
                                { value: 'rejected', text: translations.serviceStateRejected },
                            ]}
                            value={state || undefined}
                            onChange={(value) => setState(value)}
                        />
                        {errors.state.length > 0 && <ErrorMessages messages={errors.state} />}
                    </div>
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                </div>

                {/* scheduleType */}
                <div className="col-md-6 col-12">
                    <div>
                        <Typography color="muted" fontSize="12">
                            <>{translations.scheduleTypeLabel}*</>
                        </Typography>
                        <Select
                            classes="w-100"
                            type={SelectType.variant1}
                            disabled={isLoadingMyAccountServiceDetail}
                            options={[
                                { value: '', text: translations.default },
                                { value: 'pre_book', text: translations.scheduleTypePreBook },
                                { value: 'external_link', text: translations.scheduleTypeExternalLink },
                                servicePaymentsSetting
                                    ? { value: 'effective_reserve', text: translations.scheduleTypeEffectiveReserve }
                                    : undefined,
                                { value: 'no_schedule', text: translations.noSchedule },
                            ].filter((item) => item !== undefined)}
                            value={scheduleType || undefined}
                            onChange={(value) => setScheduleType(value)}
                        />
                        {errors.scheduleType.length > 0 && <ErrorMessages messages={errors.scheduleType} />}
                    </div>
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                </div>
                {/* link */}
                <div className="col-12">
                    <Input
                        type={InputTypes.text}
                        variant={InputVariants.labeledInput}
                        label={scheduleType === 'external_link' ? `${translations.linkLabel}*` : translations.linkLabel}
                        value={link}
                        onChange={(value) => setLink(value)}
                        disabled={isLoadingMyAccountServiceDetail || scheduleType !== 'external_link'}
                        state={errors.link.length > 0 ? InputStates.error : undefined}
                        errorMessages={errors.link}
                    />
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                </div>

                {/* serviceHours */}
                <div className="col-md-6 col-12">
                    <Typography color="muted" fontSize="12">
                        <>{`${translations.serviceHoursLabel}*`}</>
                    </Typography>
                    <Select
                        classes="w-100"
                        type={SelectType.variant1}
                        disabled={isLoadingMyAccountServiceDetail}
                        options={[
                            { value: '', text: translations.default },
                            ...[
                                '0',
                                '1',
                                '2',
                                '3',
                                '4',
                                '5',
                                '6',
                                '7',
                                '8',
                                '9',
                                '10',
                                '11',
                                '12',
                                '13',
                                '14',
                                '15',
                                '16',
                                '17',
                                '18',
                                '19',
                                '20',
                                '21',
                                '22',
                                '23',
                                '24',
                            ].map((hour) => ({
                                value: hour,
                                text: hour,
                            })),
                        ]}
                        value={serviceHours}
                        onChange={handleServiceHoursChange}
                    />
                    {errors.serviceHours.length > 0 && <ErrorMessages messages={errors.serviceHours} />}
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                </div>

                {/* serviceMinutes */}
                <div className="col-md-6 col-12">
                    <Typography color="muted" fontSize="12">
                        <>{`${translations.serviceMinutesLabel}*`}</>
                    </Typography>
                    <Select
                        classes="w-100"
                        type={SelectType.variant1}
                        disabled={isLoadingMyAccountServiceDetail || serviceHours === '24'}
                        options={[
                            { value: '', text: translations.default },
                            ...['0', '15', '30', '45'].map((minute) => ({
                                value: minute,
                                text: minute,
                            })),
                        ]}
                        value={serviceMinutes}
                        onChange={handleServiceMinutesChange}
                    />
                    {errors.serviceMinutes.length > 0 && <ErrorMessages messages={errors.serviceMinutes} />}
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                </div>

                {/* is cumulative (can be booked/pre booked with other services) */}
                <div className="col-md-6 col-12">
                    <Typography color="muted" fontSize="12">
                        <>{translations.isCumulativeLabel}</>
                    </Typography>
                    <Select
                        classes="w-100"
                        type={SelectType.variant1}
                        disabled={isLoadingMyAccountServiceDetail}
                        options={[
                            { value: '1', text: translations.isCumulative },
                            { value: '0', text: translations.isNotCumulative },
                        ]}
                        value={isCumulative ? '1' : '0'}
                        onChange={(value) => setIsCumulative(value === '0' ? false : true)}
                    />
                    {errors.serviceHours.length > 0 && <ErrorMessages messages={errors.serviceHours} />}
                    <Typography color="muted" fontSize="12">
                        <>{translations.isCumulativeDescription}</>
                    </Typography>
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                </div>

                {/* price */}
                <div className="col-md-6 col-12 pt-sm-2 mt-sm-1">
                    <Input
                        type={InputTypes.number}
                        variant={InputVariants.labeledInput}
                        label={scheduleType === 'effective_reserve' ? `${translations.priceLabel}*` : translations.priceLabel}
                        value={`${price}`}
                        onChange={(value) => setPrice(parseFloat(value))}
                        disabled={isLoadingMyAccountServiceDetail}
                        state={errors.price.length > 0 ? InputStates.error : undefined}
                        errorMessages={errors.price}
                    />
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                </div>

                {!automaticTranslations && (
                    <TranslationTabs
                        contentByLanguage={languagesToTranslate.map((language) => {
                            return {
                                language: language.language_code || '',
                                content: (
                                    <div className="row">
                                        <div className="col-12">
                                            <div className="d-flex flex-column gap-3">
                                                <Input
                                                    type={InputTypes.text}
                                                    label={translations.nameTranslatedLabel}
                                                    variant={InputVariants.labeledInput}
                                                    value={
                                                        nameTranslations.find(
                                                            (item) => item.languageCode === language.language_code
                                                        )?.translation || ''
                                                    }
                                                    onChange={(value) =>
                                                        setNameTranslations((previous) =>
                                                            updateTranslation(previous, language.language_code || '', value)
                                                        )
                                                    }
                                                />
                                                <Input
                                                    type={InputTypes.text}
                                                    label={translations.descriptionTranslatedLabel}
                                                    variant={InputVariants.labeledInput}
                                                    value={
                                                        descriptionTranslations.find(
                                                            (item) => item.languageCode === language.language_code
                                                        )?.translation || ''
                                                    }
                                                    onChange={(value) =>
                                                        setDescriptionTranslations((previous) =>
                                                            updateTranslation(previous, language.language_code || '', value)
                                                        )
                                                    }
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ),
                            };
                        })}
                    />
                )}

                <Space spaceBreakpoints={{ xs: { space: 15 } }} />
            </div>

            {/* submit */}
            <div className="col-12 text-end">
                <Button
                    icon="fa-solid fa-floppy-disk"
                    label={translations.submitButton}
                    mode={ButtonMode.primary}
                    disabled={isLoadingMyAccountServiceDetail}
                    onClick={() => handleOnClickSaveService()}
                />
            </div>
        </Form>
    );
};
